import isNil from "lodash/isNil";
import { useBookingModuleStore, useCalendarRoot } from "PFApp/booking/store";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { DropdownOption } from "PFComponents/dropdown";
import { OrderOption } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { getSelectedColumns, isCustomFieldOption } from "../table_properties/table_properties.utils";
import { getOrderId } from "./order_selector.utils";

const ENTRIES_LIMIT_FOR_ORDER = 3000;
export const LIMITED_ORDER_NAMES = ["billable_utilization", "available_minutes", "custom_fields"];

type GetCustomFieldDisabledData = (order: OrderOption) => { disabled: boolean; tooltipMessage?: string };
type UseOrderOptionMapperReturn = (option: OrderOption) => DropdownOption;

const isCustomFieldOrderOption = (orderName: string[]): boolean => {
  if (orderName.length > 1) {
    return false;
  }
  return isCustomFieldOption(orderName[0]);
};

export const useOrderOptionMapper = (): UseOrderOptionMapperReturn => {
  const calendarRoot = useCalendarRoot();
  const filteredProfilesCount = useBookingModuleStore((state) =>
    isWorkforceProfiles(calendarRoot)
      ? state[CalendarRoot.WorkforceProfiles].counters?.filteredProfilesCount
      : null
  );
  const filteredActivitiesCount = useBookingModuleStore((state) =>
    isProjects(calendarRoot) ? state[calendarRoot].counters?.filteredActivitiesCount : null
  );
  const {
    engagements: engagementsColumns,
    roles: rolesColumns,
    profiles: profilesColumns
  } = useBookingModuleStore((state) => state[calendarRoot].columns);

  const { selectedColumns: selectedEngagementsColumns, availableColumns: availableEngagementsColumns } =
    engagementsColumns;
  const { selectedColumns: selectedRolesColumns, availableColumns: availableRolesColumns } = rolesColumns;
  const { selectedColumns: selectedProfilesColumns, availableColumns: availableProfilesColumns } =
    profilesColumns;

  const { t } = useTranslation("bookingModule", { keyPrefix: "calendar" });

  const getColumns = useCallback(() => {
    if (isWorkforceProfiles(calendarRoot)) {
      return {
        availableColumns: availableProfilesColumns,
        selectedColumns: getSelectedColumns(availableProfilesColumns, selectedProfilesColumns)
      };
    } else {
      if (calendarRoot === CalendarRoot.ProjectsRoles) {
        return {
          availableColumns: availableRolesColumns,
          selectedColumns: getSelectedColumns(availableRolesColumns, selectedRolesColumns)
        };
      } else if (calendarRoot === CalendarRoot.ProjectsEngagements) {
        return {
          availableColumns: availableEngagementsColumns,
          selectedColumns: getSelectedColumns(availableEngagementsColumns, selectedEngagementsColumns)
        };
      }
    }
    return { availableColumns: [], selectedColumns: [] };
  }, [
    calendarRoot,
    availableProfilesColumns,
    availableRolesColumns,
    availableEngagementsColumns,
    selectedProfilesColumns,
    selectedRolesColumns,
    selectedEngagementsColumns
  ]);

  const getCustomFieldDisabledData = useCallback<GetCustomFieldDisabledData>(
    (order) => {
      const isCustomFieldOrder = isCustomFieldOrderOption(order.name);
      if (!isCustomFieldOrder) {
        return { disabled: false };
      }
      const { availableColumns, selectedColumns } = getColumns();
      const orderableTableColumn = (availableColumns ?? []).find(
        ({ orderKey }) => orderKey === order.name[0]
      );
      if (!orderableTableColumn) {
        return { disabled: true };
      }
      const disabled = !selectedColumns.some(({ orderKey }) => orderKey === order.name[0]);
      return {
        disabled,
        tooltipMessage: disabled
          ? t("sorting.disabledTooltipByCustomField", {
              customFieldName: orderableTableColumn.title,
              orderName: order.text
            })
          : undefined
      };
    },
    [t, getColumns]
  );

  return (option) => {
    const orderName = option.name.join();
    const isLimited = LIMITED_ORDER_NAMES.some((name) => orderName.includes(name));
    const entriesCount = isWorkforceProfiles(calendarRoot) ? filteredProfilesCount : filteredActivitiesCount;
    const isDisabledByLimit =
      isLimited && !isNil(entriesCount) && (entriesCount as number) > ENTRIES_LIMIT_FOR_ORDER;

    const { disabled: isDisabledByCustomField, tooltipMessage } = getCustomFieldDisabledData(option);

    const tooltipMsg = isDisabledByLimit
      ? t("sorting.disabledTooltip")
      : isDisabledByCustomField
        ? tooltipMessage
        : undefined;

    return {
      id: getOrderId(option),
      item: option,
      displayElement: option.text,
      disabled: isDisabledByLimit || isDisabledByCustomField,
      tooltipMsg
    };
  };
};
